/**
 * <contact-drawer>
 * Light-DOM wrapper around the native <dialog> used as the header contact
 * drawer (CU-86cbe5fr5). Same pattern as <mobile-menu-drawer>
 * (assets/mobile-menu-drawer.js, see docs/header.md): the dialog itself
 * provides focus trapping, Esc-to-close and an inert background for free
 * (showModal). Unlike the menu drawer, the opener is not one fixed button:
 * ANY link/button on the page can carry [data-contact-drawer-open] (header
 * CTA, mobile mail icon, services pages' contact CTAs...), so this
 * component wires them with document-level event delegation instead of a
 * single querySelector.
 */
class ContactDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.closeButton = null;
    this.opener = null;
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.closeButton = this.querySelector('[data-contact-drawer-close]');

    document.addEventListener('click', this.handleDocumentClick);

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }

    // Closing on backdrop click: the native ::backdrop isn't a real element,
    // clicking it fires a click on the dialog itself with the dialog as target.
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('close', this.handleDialogClose);
    this.dialog.addEventListener('keydown', this.handleKeydown);

    if (window.location.hash === '#hablamos') {
      this.open();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }

    if (this.dialog) {
      this.dialog.removeEventListener('click', this.handleDialogClick);
      this.dialog.removeEventListener('close', this.handleDialogClose);
      this.dialog.removeEventListener('keydown', this.handleKeydown);
    }
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;

    this.opener = opener || null;
    this.dialog.showModal();

    const firstField = this.dialog.querySelector('[data-contact-form-field] input, [data-contact-form-field] textarea');
    if (firstField) firstField.focus();
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;
    this.dialog.close();
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-contact-drawer-open]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  handleCloseClick() {
    this.close();
  }

  handleDialogClick(event) {
    // Only the desktop panel has a scrim to click on; the mobile sheet fills
    // the viewport (Figma 1219:17211 has no click-outside affordance).
    if (event.target === this.dialog && this.desktopQuery.matches) {
      this.close();
    }
  }

  handleDialogClose() {
    if (this.opener) {
      this.opener.focus();
      this.opener = null;
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}

customElements.define('contact-drawer', ContactDrawer);
